import { useCallback, useRef } from "react";
import type { User } from "../AuthContext";
import type { PropertySubscription } from "../PropertySubscriptionContext";
import { logger } from "@/utils/debugLogger";
import { hasActivePropertyPackage } from "@/utils/subscriptionState";

interface UseSubscriptionSelectorsParams {
  propertySubscriptions: PropertySubscription[];
  user: User | null;
}

export interface SubscriptionSelectors {
  getPropertySubscription: (propertyId: string) => PropertySubscription | null;
  canAccessFeature: (propertyId: string, feature: string) => boolean;
  canRunSimulation: (propertyId: string) => boolean;
  getAnnualCostForProperties: (propertyIds: string[]) => number;
}

export function useSubscriptionSelectors({
  propertySubscriptions,
  user,
}: UseSubscriptionSelectorsParams): SubscriptionSelectors {
  // Stable per-property admin-access objects. Admins have genuine full access to
  // every property (matching canAccessFeature + the backend data layer, which
  // short-circuits on is_superuser/user_type==='admin'), but the subscription-list
  // endpoint returns only the user's own ForestAccess rows and does not synthesize
  // admin access. Cache the synthetic entry per property so its identity is stable
  // across renders (no effect-dep churn / render loops).
  const adminAccessCacheRef = useRef<Record<string, PropertySubscription>>({});

  const getPropertySubscription = useCallback(
    (propertyId: string): PropertySubscription | null => {
      // Normalize property IDs for comparison to handle format variations
      const normalizePropertyId = (id: string): string => {
        // Remove leading/trailing whitespace and convert to lowercase
        // Keep hyphens as they are part of the property ID format
        return id.trim().toLowerCase();
      };

      const normalizedSearchId = normalizePropertyId(propertyId);

      logger.debug(
        {
          component: "PropertySubscriptionContext",
          action: "getPropertySubscription",
        },
        `Searching for property: "${propertyId}" (normalized: "${normalizedSearchId}")`,
        { availableSubscriptions: propertySubscriptions.length },
      );

      // Debug property matching
      logger.debugPropertyMatching(propertyId, propertySubscriptions);

      const subscription = propertySubscriptions.find((sub) => {
        const normalizedSubId = normalizePropertyId(sub.property_id);

        // Direct match after normalization
        if (normalizedSubId === normalizedSearchId) {
          logger.debug(
            { component: "PropertySubscriptionContext", propertyId },
            `Found exact match: ${sub.property_id}`,
          );
          return true;
        }

        // Also check if one format uses dashes and the other doesn't
        // e.g., "178-415-5-2" vs "17841552"
        const withoutDashes = (id: string) => id.replace(/[-_.]/g, "");
        if (
          withoutDashes(normalizedSubId) === withoutDashes(normalizedSearchId)
        ) {
          logger.debug(
            { component: "PropertySubscriptionContext", propertyId },
            `Found normalized match: ${sub.property_id}`,
          );
          return true;
        }

        return false;
      });

      if (subscription) {
        return subscription;
      }

      // Admin/superuser: genuine full access to every property. Return a real
      // admin-access entry (pro tier) so the gates that read this — and thus the whole
      // page (implement, price-estimate) — unlock, consistent with canAccessFeature
      // and the backend data layer. This is the admin's true elevated entitlement,
      // not fabricated customer data. Cached above for stable identity.
      if (user?.user_type === "admin") {
        const cache = adminAccessCacheRef.current;
        if (!cache[propertyId]) {
          cache[propertyId] = {
            id: `admin-access-${propertyId}`,
            property_id: propertyId,
            property_name: "",
            municipality: "",
            tier: "pro",
            tier_display: "Admin",
            package_state: "active",
            // Administrator access carries no expiry, so it is never expiring.
            package_expiring: false,
            annual_cost: 0,
            started_at: "",
            is_expired: false,
            is_active: true,
            simulations_used: 0,
            can_run_simulation: true,
            auto_renew: false,
          };
        }
        return cache[propertyId];
      }

      // Missing subscription is a common/expected access-control state.
      // Keep this at debug level to avoid warning noise in normal flows.
      logger.debug(
        { component: "PropertySubscriptionContext", propertyId },
        `No subscription found for property: ${propertyId}`,
      );
      return null;
    },
    [propertySubscriptions, user?.user_type],
  );

  const canAccessFeature = useCallback(
    (propertyId: string, feature: string): boolean => {
      // Admin/superuser bypass: full access to all features
      if (user?.user_type === "admin") {
        return true;
      }

      const subscription = getPropertySubscription(propertyId);
      if (
        !subscription ||
        subscription.is_expired ||
        subscription.is_active === false
      ) {
        return false;
      }

      if (feature === "alerts") {
        return hasActivePropertyPackage(subscription);
      }

      if (feature === "offer_decode") {
        return subscription.sku_key === "offer_decode";
      }

      if (
        subscription.features &&
        subscription.features[feature] !== undefined
      ) {
        return subscription.features[feature];
      }

      // The server owns entitlement semantics: every row of
      // GET /api/users/property-subscriptions/ carries the full `features`
      // map in both billing modes (backend_django/users/
      // property_subscription_views.py). A row that does not state this
      // feature is an entitlement we cannot determine, not a tier we may
      // re-derive here — deriving it locally would be a second entitlement
      // authority that can disagree with the gateway. Fail closed, loudly.
      logger.warn(
        { component: "PropertySubscriptionContext", propertyId },
        `Entitlement row states no '${feature}' feature — denying access`,
      );
      return false;
    },
    [user?.user_type, getPropertySubscription],
  );

  const canRunSimulation = useCallback(
    (propertyId: string): boolean => {
      // Admin/superuser bypass: unlimited simulations
      if (user?.user_type === "admin") {
        return true;
      }

      const subscription = getPropertySubscription(propertyId);
      return subscription && subscription.is_active !== false
        ? subscription.can_run_simulation
        : false;
    },
    [user?.user_type, getPropertySubscription],
  );

  const getAnnualCostForProperties = useCallback(
    (propertyIds: string[]): number => {
      return propertyIds.reduce((total, propertyId) => {
        const subscription = getPropertySubscription(propertyId);
        return total + (subscription ? subscription.annual_cost : 0);
      }, 0);
    },
    [getPropertySubscription],
  );

  return {
    getPropertySubscription,
    canAccessFeature,
    canRunSimulation,
    getAnnualCostForProperties,
  };
}
